import { toClassName, type NodeOrFn, renderNodeOrFn, toClassListRecord } from "@mdotm/shadcn/legacy/react-extensions";
import { CircularProgressBar, Text, type InnerRefProps, type StylableProps } from "@mdotm/shadcn/legacy/components";
import React, { useMemo, type ReactNode } from "react";
import { InfoTooltip } from "./WidgetsMapper/InfoTooltip";

export type CardProps = StylableProps &
	InnerRefProps<HTMLDivElement> & {
		title?: NodeOrFn;
		subTitle?: NodeOrFn;
		children: NodeOrFn;
		onMouseDown?: React.MouseEventHandler<HTMLDivElement>;
		onMouseUp?: React.MouseEventHandler<HTMLDivElement>;
		onTouchEnd?: React.TouchEventHandler<HTMLDivElement>;
		footer?: NodeOrFn;
		actionHeader?: NodeOrFn;
		midActionHeader?: NodeOrFn;
		tooltip?: ReactNode;
		removeDefaultPaddings?: boolean;
		isLoading?: boolean;
		id?: string;
	};

export function Card({
	title,
	subTitle,
	children,
	footer,
	actionHeader,
	midActionHeader,
	tooltip,
	style,
	className,
	innerRef,
	removeDefaultPaddings = false,
	isLoading,
	id,
	...handlers
}: CardProps): React.JSX.Element {
	const renderedTitle = useMemo(() => {
		if (!title) {
			return undefined;
		}
		const rendered = renderNodeOrFn(title);
		if (typeof rendered === "string") {
			return (
				<Text type="Body/XL/Bold" title={rendered} className="truncate">
					{rendered}
				</Text>
			);
		}
		return rendered;
	}, [title]);
	const renderedMidActions = renderNodeOrFn(midActionHeader);
	return (
		<div
			className={toClassName({
				"rounded-sm bg-white flex flex-col min-h-0 grow": true,
				"px-4 pb-4": !removeDefaultPaddings,
				...toClassListRecord(className),
			})}
			style={style}
			ref={innerRef}
			id={id}
			data-loading={isLoading}
			{...handlers}
		>
			{renderedTitle || actionHeader || tooltip != null || isLoading ? (
				<div className="flex items-center justify-between py-[10px] Header overflow-hidden">
					<div className="flex items-center gap-2 min-w-0">
						{renderedTitle}
						{tooltip != null && <InfoTooltip>{tooltip}</InfoTooltip>}
					</div>
					{renderedMidActions && <div className="flex items-center gap-2 ml-2">{renderedMidActions}</div>}
					<div className="flex items-center gap-2 ml-2">
						{isLoading && <CircularProgressBar value="indeterminate" outerDiameter={16} />}
						{renderNodeOrFn(actionHeader)}
					</div>
				</div>
			) : (
				!removeDefaultPaddings && <div className="pt-3" />
			)}
			<div className="min-h-0 flex flex-col flex-1 children-and-subtitle-wrapper">
				{subTitle && <div className="text-[14px] pb-1.5">{renderNodeOrFn(subTitle)}</div>}
				<div className="min-h-0 flex flex-col flex-1 children-wrapper">{renderNodeOrFn(children)}</div>
			</div>
			{footer && <div>{renderNodeOrFn(footer)}</div>}
		</div>
	);
}
